import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const AdminSubjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    coefficient: 1,
    is_active: true,
  });

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/subjects', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Erreur matières:', e);
      showToast('Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredSubjects = useMemo(() => {
    return subjects.filter((s) => {
      if (searchTerm) {
        return (s.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });
  }, [subjects, searchTerm]);

  const stats = useMemo(() => {
    const total = subjects.length;
    const totalCoef = subjects.reduce((sum, s) => sum + (parseInt(s.coefficient) || 1), 0);
    const active = subjects.filter((s) => s.is_active !== false).length;
    return { total, totalCoef, active };
  }, [subjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        coefficient: parseInt(formData.coefficient) || 1,
        is_active: formData.is_active,
      };

      if (editingSubject) {
        await api.put(`/subjects/${editingSubject.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast('Matière modifiée avec succès !', 'success');
      } else {
        await api.post('/subjects', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast(`Matière "${formData.name}" créée avec succès !`, 'success');
      }
      closeModal();
      fetchSubjects();
    } catch (error) {
      console.error('Erreur:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Erreur lors de l'enregistrement";
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette matière ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/subjects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Matière supprimée avec succès !', 'success');
      fetchSubjects();
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name || '',
      coefficient: subject.coefficient || 1,
      is_active: subject.is_active !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSubject(null);
    setFormData({ name: '', coefficient: 1, is_active: true });
  };

  const handleNew = () => {
    setEditingSubject(null);
    setFormData({ name: '', coefficient: 1, is_active: true });
    setShowModal(true);
  };

  const SUGGESTIONS = [
    'Mathématiques', 'Français', 'Anglais', 'SVT',
    'Histoire-Géographie', 'Physique-Chimie', 'EPS',
    'Philosophie', 'Arabe', 'Espagnol', 'Informatique',
  ];

  return (
    <div className="p-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}
        >
          <span className="text-xl">{toast.type === 'success' ? '✅' : '❌'}</span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="text-white text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">📖 Gestion des matières</h1>
          <p className="text-gray-500">Créez et gérez les matières de votre établissement</p>
        </div>
        <button
          onClick={handleNew}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium"
        >
          ➕ Nouvelle matière
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">📚 Matières</p>
          <p className="text-2xl font-bold text-[#2C3E50]">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">✅ Actives</p>
          <p className="text-2xl font-bold text-green-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
          <p className="text-xs text-gray-500 mb-1">📊 Total coefficients</p>
          <p className="text-2xl font-bold text-orange-600">{stats.totalCoef}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 mb-4">
        <input
          type="text"
          placeholder="🔍 Rechercher une matière..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        />
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Chargement...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matière</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Coefficient</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubjects.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-8 text-gray-500">
                      {subjects.length === 0
                        ? 'Aucune matière. Commencez par en créer une.'
                        : 'Aucun résultat'}
                    </td>
                  </tr>
                ) : (
                  filteredSubjects.map((s) => (
                    <tr key={s.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-[#2C3E50]">
                        📖 {s.name}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-orange-100 text-[#E67E22] rounded-full text-xs font-medium">
                          Coef {s.coefficient}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {s.is_active !== false ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            ● Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            ○ Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleEdit(s)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-4">
              {editingSubject ? '✏️ Modifier la matière' : '➕ Nouvelle matière'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la matière *
                </label>
                <input
                  type="text"
                  placeholder="Ex: Mathématiques"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                />
              </div>

              {!editingSubject && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Suggestions rapides :</p>
                  <div className="flex flex-wrap gap-1">
                    {SUGGESTIONS.filter(
                      (s) => !subjects.some((sub) => sub.name === s)
                    ).slice(0, 6).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setFormData({ ...formData, name: s })}
                        className="text-xs bg-gray-100 hover:bg-orange-100 text-gray-700 px-2 py-1 rounded"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coefficient *
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.coefficient}
                  onChange={(e) =>
                    setFormData({ ...formData, coefficient: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  Le coefficient multiplie le poids de la matière dans la moyenne.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === true}
                      onChange={() => setFormData({ ...formData, is_active: true })}
                      className="accent-[#E67E22]"
                    />
                    <span className="text-sm">● Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === false}
                      onChange={() => setFormData({ ...formData, is_active: false })}
                      className="accent-[#E67E22]"
                    />
                    <span className="text-sm">○ Inactive</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] font-medium"
                >
                  {editingSubject ? '💾 Modifier' : '✅ Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubjects;